import moment from "moment";
import dateFormats from "app/data/common/dateFormats";
import { downloadCsvFile } from "app/data/common/exportToCsv";
import { LoadboardListManifestTrip } from "app/data/load-board/models";

const formatUiDate = (value?: string | null): string => {
  if (!value) return "";
  return (value as any).toUiDateFormat?.() ?? value;
};

const formatUiTime = (value?: string | null): string => {
  if (!value) return "";
  return (value as any).toUiTimeFormat?.() ?? value;
};

const formatTerminal = (
  airportCode?: string | null,
  name?: string | null,
  number?: string | number | null
): string => {
  const left = [airportCode, name].filter(Boolean).join(" - ");
  if (left && number != null && number !== "") return `${left} (${number})`;
  if (left) return left;
  if (number != null && number !== "") return String(number);
  return "";
};

const formatLoadPercent = (loadedWeight?: number | null): string => {
  const weight = Number(loadedWeight) || 0;
  const MAX_CAPACITY = 42000;
  const pct = Math.min(Math.round((weight / MAX_CAPACITY) * 100), 100);
  return `${pct}%`;
};

/**
 * Build CSV from Load Board parent manifest rows currently visible in the grid.
 */
const exportLoadBoardToCsv = (
  rows: LoadboardListManifestTrip[],
  tripsByManifest?: Map<number | string, LoadboardListManifestTrip[]>
): void => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const headers = [
    "Manifest #",
    "Door",
    "Priority",
    "Trips",
    "Origin Airport Code",
    "Origin Terminal Name",
    "Origin Terminal Number",
    "Stop",
    "Destination Airport Code",
    "Destination Terminal Name",
    "Destination Terminal Number",
    "Lane Status",
    "Load %",
    "Loaded Weight",
    "Carrier Name",
    "Carrier Number",
    "Plan Close Date",
    "Plan Close Time",
    "Dispatch Date",
    "Dispatch Time",
    "Driver Name",
    "Driver ID",
    "Trailer",
    "Tractor",
    "Timezone",
  ];

  const csvRows = rows.map((row) => {
    const trips =
      tripsByManifest?.get(Number(row.manifestNumber)) ??
      tripsByManifest?.get(row.manifestNumber as any) ??
      [];
    const sorted = [...trips].sort(
      (a, b) => Number(a.tripNumber) - Number(b.tripNumber)
    );
    const first = sorted[0];
    const middle = sorted.length >= 2 ? sorted[1] : null;
    const last = sorted[sorted.length - 1];
    const highestTrip = sorted.length
      ? Math.max(...sorted.map((t) => Number(t.tripNumber)))
      : row.tripNumber ?? "";

    const loadedWeight = Number((row as any).loadedWeight) || 0;

    return [
      row.manifestNumber ?? "",
      row.doorNumber ? `D-${row.doorNumber}` : "",
      (row as any).doorPriority ?? "",
      highestTrip,
      first?.originTerminalAirportCode ?? row.originTerminalAirportCode ?? "",
      first?.originTerminalName ?? row.originTerminalName ?? "",
      first?.originTerminalNumber ?? row.originTerminalNumber ?? "",
      middle
        ? formatTerminal(
            middle.originTerminalAirportCode,
            middle.originTerminalName,
            middle.originTerminalNumber
          )
        : "",
      last?.destinationTerminalAirportCode ?? row.destinationTerminalAirportCode ?? "",
      last?.destinationTerminalName ?? row.destinationTerminalName ?? "",
      last?.destinationTerminalNumber ?? row.destinationTerminalNumber ?? "",
      row.doorManifestStatus ?? "",
      formatLoadPercent(loadedWeight),
      loadedWeight || "",
      row.carrierName ?? "",
      row.carrierNumber ?? "",
      formatUiDate(row.plannedCloseDate),
      formatUiTime(row.plannedCloseTime),
      formatUiDate(row.dispatchDate),
      formatUiTime(row.dispatchTime),
      row.driverName ?? row.driver ?? "",
      row.driverId ?? "",
      row.trailerNumber ? `TRL - ${row.trailerNumber}` : "",
      row.tractorNumber ? `TRC - ${row.tractorNumber}` : "",
      timezone,
    ];
  });

  downloadCsvFile(
    `loadboard-manifests-${moment().format(dateFormats.uiDate)}.csv`,
    headers,
    csvRows
  );
};

export default exportLoadBoardToCsv;
